// Story 1.30 — TanStack Query hook for GET /api/v1/admin/bulletin/posts.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type BulletinStatus = "draft" | "published" | "any";

export type BulletinListFilters = {
  status: BulletinStatus;
  title_search?: string;
  page: number;
  page_size: number;
};

export const DEFAULT_BULLETIN_FILTERS: BulletinListFilters = {
  status: "any",
  page: 1,
  page_size: 50,
};

export type BulletinPostListItem = {
  id: string;
  slug: string;
  title: string;
  body_md: string;
  linked_dossier_id: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
};

export type BulletinListResponse = {
  data: BulletinPostListItem[];
  page: number;
  page_size: number;
  has_next: boolean;
};

function buildQueryString(filters: BulletinListFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.title_search) params.set("title_search", filters.title_search);
  params.set("page", String(filters.page));
  params.set("page_size", String(filters.page_size));
  return params.toString();
}

async function fetchBulletinList(
  filters: BulletinListFilters
): Promise<BulletinListResponse> {
  const qs = buildQueryString(filters);
  const response = await fetch(`/api/v1/admin/bulletin/posts?${qs}`, {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Admin bulletin list fetch failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  return (await response.json()) as BulletinListResponse;
}

export function useAdminBulletinList(filters: BulletinListFilters) {
  return useQuery<BulletinListResponse, Error>({
    queryKey: ["admin-bulletin-list", filters],
    queryFn: () => fetchBulletinList(filters),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}
